"use client";

import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { CustomerFilters } from "@/types/models/customer";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

interface CustomerFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: CustomerFilters) => void;
  currentFilters: CustomerFilters;
}

// Form validation schema
const filterSchema = z.object({
  search: z.string().optional(),
  total_orders_min: z.number().min(0, "Must be 0 or greater").optional(),
  total_orders_max: z.number().min(0, "Must be 0 or greater").optional(),
  total_spent_min: z.number().min(0, "Must be 0 or greater").optional(),
  total_spent_max: z.number().min(0, "Must be 0 or greater").optional(),
  created_after: z.string().optional(),
  created_before: z.string().optional(),
  last_active_after: z.string().optional(),
  last_active_before: z.string().optional(),
  has_email: z.boolean().optional(),
  has_phone: z.boolean().optional(),
});

const CustomerFilterModal: React.FC<CustomerFilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerFilters>({
    resolver: zodResolver(filterSchema),
    defaultValues: currentFilters,
  });

  React.useEffect(() => {
    if (isOpen) {
      reset(currentFilters);
    }
  }, [isOpen, currentFilters, reset]);

  const onSubmit = (data: CustomerFilters) => {
    // Remove empty values
    const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key as keyof CustomerFilters] = value;
      }
      return acc;
    }, {} as CustomerFilters);

    onApplyFilters(cleanedData);
    onClose();
  };

  const handleClear = () => {
    reset();
    onApplyFilters({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filter Customers</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Order Statistics */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Order Statistics</h4>
            <div className="grid grid-cols-2 gap-4">
              <StandardFormInput
                label="Min Orders"
                type="number"
                placeholder="0"
                {...register("total_orders_min", { valueAsNumber: true })}
                error={errors.total_orders_min?.message}
              />
              <StandardFormInput
                label="Max Orders"
                type="number"
                placeholder="100"
                {...register("total_orders_max", { valueAsNumber: true })}
                error={errors.total_orders_max?.message}
              />
            </div>
          </div>

          {/* Spending Statistics */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Spending Statistics</h4>
            <div className="grid grid-cols-2 gap-4">
              <StandardFormInput
                label="Min Spent (in paise)"
                type="number"
                placeholder="0"
                {...register("total_spent_min", { valueAsNumber: true })}
                error={errors.total_spent_min?.message}
              />
              <StandardFormInput
                label="Max Spent (in paise)"
                type="number"
                placeholder="100000"
                {...register("total_spent_max", { valueAsNumber: true })}
                error={errors.total_spent_max?.message}
              />
            </div>
          </div>

          {/* Date Ranges */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Registration Date</h4>
            <div className="grid grid-cols-2 gap-4">
              <StandardFormInput
                label="Created After"
                type="date"
                {...register("created_after")}
                error={errors.created_after?.message}
              />
              <StandardFormInput
                label="Created Before"
                type="date"
                {...register("created_before")}
                error={errors.created_before?.message}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Last Activity</h4>
            <div className="grid grid-cols-2 gap-4">
              <StandardFormInput
                label="Active After"
                type="date"
                {...register("last_active_after")}
                error={errors.last_active_after?.message}
              />
              <StandardFormInput
                label="Active Before"
                type="date"
                {...register("last_active_before")}
                error={errors.last_active_before?.message}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Contact Information</h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="has_email"
                  {...register("has_email")}
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="has_email" className="text-sm text-foreground">
                  Has email address
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="has_phone"
                  {...register("has_phone")}
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="has_phone" className="text-sm text-foreground">
                  Has phone number
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={handleClear}>
              Clear All
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                Apply Filters
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerFilterModal;